import update from 'immutability-helper';

import { calculateRearrangementAfterReplacement } from '../../../../configurator/nodes/slotLine/slotLinePositionCalculator';
import { HEIGHT_CONTROL_PANEL_UUID, HEIGHT_CONTROL_PANEL_WIDTH } from '../../../../constants';
import { Article } from '../../../../interfaces/Article';
import { Configuration } from '../../../../interfaces/Configuration';
import ConnectableSlots from '../../../../interfaces/ConnectableSlots';
import { ReplacementChild } from '../../../../interfaces/ReplacementChild';
import { isSlotLine, Slot } from '../../../../interfaces/Slot';
import { SlotDefinition, SlotType } from '../../../../interfaces/SlotDefinition';
import SlotService from '../../../../services/SlotService';
import { articleService } from '../../../../services/articleService';
import { SlotDefinitionService } from '../../../../services/slotDefinitionService';
import { removeArticleFromConfiguration } from '../remove/removeArticleFromConfiguration';

export const transferChildren = async (
  configuration: Configuration,
  oldArticle: Article,
  newArticle: Article,
  newArticleSlots: Slot[],
): Promise<Configuration> => {
  const newSlots = JSON.parse(JSON.stringify(newArticleSlots));

  const oldSlots = configuration.getSlotsOfArticle(oldArticle.id);
  const direction = Math.sign(newSlots.length - oldSlots.length);

  const children: ReplacementChild[] = configuration
    .getChildren(oldArticle.id)
    .map((childId) => ({ id: childId, slot: configuration.getAttachedSlot(childId).definition }))
    .filter((child) => child.slot.type === SlotType.SINGLE || child.slot.type === SlotType.PAIR)
    .sort((a, b) => compareByNumberOfSlotsByNameByXIndexes(a.slot, b.slot, oldSlots, direction));

  const { childrenToRemove, childrenToMove } = await transferChildrenToSameLocations(
    children,
    configuration,
    newSlots,
    newArticle,
  );

  childrenToRemove.push(
    ...(await transferChildrenToNewLocations(
      childrenToMove,
      configuration,
      newSlots,
      newArticle,
      direction,
    )),
  );

  childrenToRemove.push(
    ...transferChildrenOnLineSlots(configuration, oldArticle, newSlots, newArticle),
  );

  childrenToRemove.forEach((childId) => {
    configuration = removeArticleFromConfiguration(configuration, childId);
  });

  return update(configuration, {
    slots: {
      $set: [
        ...configuration.slots
          .filter((slot) => slot.parentId !== oldArticle.id)
          .map((slot) => updateIfParentSlot(slot, oldArticle.id, newArticle.id)),
        ...newSlots,
      ],
    },
  });
};

const transferChildrenToSameLocations = async (
  children: ReplacementChild[],
  configuration: Configuration,
  newSlots: Slot[],
  newArticle: Article,
) => {
  const result = { childrenToMove: [], childrenToRemove: [] };

  for (const child of children) {
    const { id: childId, slot } = child;

    if (slot.type === SlotType.SINGLE) {
      const possibleSlots = await articleService.getPossibleArticlePositions(
        configuration,
        childId,
        newSlots,
        newArticle.number,
      );

      if (!possibleSlots || !possibleSlots.length) {
        result.childrenToRemove.push(childId);
        continue;
      }

      const newSlotOnSamePosition = possibleSlots.find((slots) =>
        SlotDefinitionService.areEqual(slot, slots.mainSlot.definition),
      );

      newSlotOnSamePosition
        ? transferToSlot(childId, newSlotOnSamePosition, newSlots)
        : result.childrenToMove.push(child);

      continue;
    }

    if (slot.type === SlotType.PAIR) {
      const newSlot = newSlots.find((newSlot) =>
        SlotDefinitionService.areEqual(slot, newSlot.definition),
      );

      if (!newSlot || newSlot.type !== SlotType.PAIR) {
        result.childrenToRemove.push(childId);
        continue;
      }

      newSlot.childId = childId;
    }
  }

  return result;
};

const transferChildrenToNewLocations = async (
  childrenToMove: ReplacementChild[],
  configuration: Configuration,
  newSlots: Slot[],
  newArticle: Article,
  direction: number,
): Promise<string[]> => {
  const childrenToRemove = [];

  for (const child of childrenToMove) {
    const { id: childId, slot } = child;

    if (slot.type === SlotType.SINGLE) {
      const possibleSlots = await articleService.getPossibleArticlePositions(
        configuration,
        childId,
        newSlots,
        newArticle.number,
      );

      if (possibleSlots && possibleSlots.length) {
        const newPosition = possibleSlots.sort((a, b) =>
          compareXIndexes(a.mainSlot.definition, b.mainSlot.definition, direction),
        )[0];

        transferToSlot(childId, newPosition, newSlots);
      } else {
        childrenToRemove.push(childId);
      }
    }
  }

  return childrenToRemove;
};

const transferChildrenOnLineSlots = (
  configuration: Configuration,
  oldArticle: Article,
  newSlots: Slot[],
  newArticle: Article,
): string[] => {
  const childrenToRemove = [];

  configuration
    .getSlotsOfArticle(oldArticle.id)
    .filter(isSlotLine)
    .forEach((oldSlotLine) => {
      const newSlotLine = newSlots.find((slot) =>
        SlotDefinitionService.areEqual(oldSlotLine.definition, slot.definition),
      );

      if (!newSlotLine || newSlotLine.type !== SlotType.LINE) {
        return;
      }

      let occupiedSpace = SlotService.calculateSlotLineOccupiedSpace(
        oldSlotLine,
        configuration.articles,
      );

      if (
        newArticle.definition.hasHeightControlPanel &&
        !oldArticle.definition.hasHeightControlPanel
      ) {
        occupiedSpace += HEIGHT_CONTROL_PANEL_WIDTH;
      }

      oldSlotLine.children
        .sort((a, b) => a.position - b.position)
        .filter((child) => child.id !== HEIGHT_CONTROL_PANEL_UUID)
        .forEach((child) => {
          if (occupiedSpace > newSlotLine.definition.length) {
            const articleChildToRemove = configuration.getArticle(child.id);

            childrenToRemove.push(child.id);

            oldSlotLine.children = oldSlotLine.children.filter((c) => c.id !== child.id);

            occupiedSpace -= articleChildToRemove.definition.width;
          }
        });

      const oldHeightControlPanel = oldSlotLine.children.find(
        (child) => child.id === HEIGHT_CONTROL_PANEL_UUID,
      );
      const newHeightControlPanel = newSlotLine.children.find(
        (child) => child.id === HEIGHT_CONTROL_PANEL_UUID,
      );

      const heightControl =
        !!oldHeightControlPanel && !!newHeightControlPanel
          ? oldHeightControlPanel
          : newHeightControlPanel;

      oldSlotLine.children = [
        heightControl,
        ...oldSlotLine.children.map((child) =>
          child.id === HEIGHT_CONTROL_PANEL_UUID ? undefined : child,
        ),
      ].filter(Boolean);

      newSlotLine.children = calculateRearrangementAfterReplacement(
        configuration.articles,
        oldSlotLine,
        newSlotLine,
      );
    });

  return childrenToRemove;
};

const transferToSlot = (childId: string, newPosition: ConnectableSlots, newSlots: Slot[]) => {
  const newMainSlot = newSlots.find((slot) => slot.id === newPosition.mainSlot.id);

  if (!newMainSlot || newMainSlot.type !== SlotType.SINGLE) {
    return;
  }

  newMainSlot.childId = childId;

  newPosition.blockedSlots.forEach((blockedSlot) => {
    const newBlockedSlot = newSlots.find((slot) => slot.id === blockedSlot.id);
    newBlockedSlot.blockedBy = childId;
  });
};

const compareXIndexes = (first: SlotDefinition, second: SlotDefinition, direction: number) => {
  if (
    first.type === SlotType.SINGLE &&
    first.xIndex !== undefined &&
    first.xIndex !== null &&
    second.type === SlotType.SINGLE &&
    second.xIndex !== undefined &&
    second.xIndex !== null
  ) {
    return direction < 0 ? second.xIndex - first.xIndex : first.xIndex - second.xIndex;
  }
  return 0;
};

const compareByNumberOfSlotsByNameByXIndexes = (
  first: SlotDefinition,
  second: SlotDefinition,
  parentSlots: Slot[],
  direction: number,
) => {
  const firstSlots = parentSlots.filter((slot) => slot.definition.name === first.name);
  const secondSlots = parentSlots.filter((slot) => slot.definition.name === second.name);
  return (
    secondSlots.length - firstSlots.length ||
    first.name.localeCompare(second.name) ||
    compareXIndexes(first, second, direction)
  );
};

const updateIfParentSlot = (slot: Slot, oldArticleId: string, newArticleId: string): Slot => {
  if (
    slot.type === SlotType.LINE &&
    slot.children.map((child) => child.id).includes(oldArticleId)
  ) {
    const children = [...slot.children];
    const index = children.findIndex((s) => s.id === oldArticleId);

    children[index].id = newArticleId;
    return {
      ...slot,
      children,
    };
  }

  if (slot.type !== SlotType.LINE && slot.childId === oldArticleId) {
    return {
      ...slot,
      childId: newArticleId,
    };
  }

  if (slot.type !== SlotType.LINE && slot.blockedBy === oldArticleId) {
    return {
      ...slot,
      blockedBy: newArticleId,
    };
  }

  return { ...slot };
};

export const childrenCanBeSaved = async (
  configuration: Configuration,
  oldArticle: Article,
  newArticle: Article,
  newArticleSlots: Slot[],
): Promise<boolean> => {
  const newSlots = JSON.parse(JSON.stringify(newArticleSlots));

  const oldSlots = configuration.getSlotsOfArticle(oldArticle.id);
  const direction = Math.sign(newSlots.length - oldSlots.length);

  const children: ReplacementChild[] = configuration
    .getChildren(oldArticle.id)
    .map((childId) => ({ id: childId, slot: configuration.getAttachedSlot(childId).definition }))
    .filter((child) => child.slot.type === SlotType.SINGLE || child.slot.type === SlotType.PAIR)
    .sort((a, b) => compareByNumberOfSlotsByNameByXIndexes(a.slot, b.slot, oldSlots, direction));

  const { childrenToRemove, childrenToMove } = await transferChildrenToSameLocations(
    children,
    configuration,
    newSlots,
    newArticle,
  );

  if (childrenToRemove.length) {
    return false;
  }

  childrenToRemove.push(
    ...(await transferChildrenToNewLocations(
      childrenToMove,
      configuration,
      newSlots,
      newArticle,
      direction,
    )),
  );

  if (childrenToRemove.length) {
    return false;
  }

  childrenToRemove.push(
    ...transferChildrenOnLineSlots(configuration, oldArticle, newSlots, newArticle),
  );

  return !childrenToRemove.length;
};

export const enoughSpaceOnLineSlots = async (
  configuration: Configuration,
  selectedArticle: Article,
  newArticle: Article,
  newSlots: Slot[],
) => {
  const lineSlotsWithChildren = configuration.slots
    .filter(isSlotLine)
    .filter((slotLine) => slotLine.children.length > 0);

  let enoughSpace = true;

  for (const lineSlot of lineSlotsWithChildren) {
    let requiredSpace = SlotService.calculateSlotLineOccupiedSpace(
      lineSlot,
      configuration.articles,
    );

    if (configuration.getRoot().id === lineSlot.parentId) {
      if (
        newArticle.definition.hasHeightControlPanel &&
        !selectedArticle.definition.hasHeightControlPanel
      ) {
        requiredSpace += HEIGHT_CONTROL_PANEL_WIDTH;
      }

      const rootLineSlotLength = newSlots.find(isSlotLine)?.definition?.length;

      if (requiredSpace > rootLineSlotLength) {
        enoughSpace = false;
      }
    } else {
      const [, newSlots] = await articleService.getArticleDependentOnRoot(
        configuration.getArticle(lineSlot.parentId).number,
        newArticle.number,
      );

      const availableSpace = newSlots
        .filter(isSlotLine)
        .find((slot) => slot.definition.name === lineSlot.definition.name)?.definition?.length;

      if (requiredSpace > availableSpace) {
        enoughSpace = false;
      }
    }
  }
  return enoughSpace;
};
